/**
 * Recover from stale-deploy chunk-load failures.
 *
 * Vite gives every build content-hashed chunk filenames and each deploy
 * deletes the previous build's chunks from the CDN. A tab left open across a
 * deploy fails the moment it lazy-loads a code-split route/component (e.g. the
 * Stripe payment form), because that chunk's old hash is gone (404/403) →
 * "Failed to fetch dynamically imported module". The user lands on a generic
 * "something went wrong" screen even though nothing is actually broken.
 *
 * Vite dispatches a `vite:preloadError` window event whenever a dynamic import
 * fails. We reload once to pull the fresh index.html + the new chunk hashes,
 * which silently recovers the user onto the current build. This covers every
 * dynamic import in the app — all `React.lazy()` call sites and the router's
 * own code-splitting — since Vite instruments them all.
 *
 * A short sessionStorage-backed debounce prevents a reload loop: if a chunk is
 * genuinely missing (a broken deploy, not just a stale tab), reloading won't
 * fix it, so after one recent reload we stop and let the error surface to the
 * route/error boundary instead of trapping the user in an infinite refresh.
 */

const RELOAD_GUARD_KEY = 'notifi:chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

function readLastReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    // sessionStorage can throw in private mode / when storage is blocked.
    return 0;
  }
}

function rememberReloadAt(now: number): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Best-effort; if storage is unavailable we simply lose the loop guard.
  }
}

export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Suppress Vite's default behavior of rethrowing as an uncaught error; the
    // failed import() promise still rejects, so a genuinely-missing chunk will
    // surface through the normal error boundary on the guard path below.
    event.preventDefault();

    const now = Date.now();
    const last = readLastReloadAt();

    // Reloaded very recently → the chunk is missing for real, not just stale.
    // Don't loop; let the error boundary handle it.
    if (now - last < RELOAD_DEBOUNCE_MS) {
      return;
    }

    rememberReloadAt(now);
    window.location.reload();
  });
}
